import { PromiseCompletionSource } from "async/PromiseCompletionSource";
import { ICanvasScaler } from "by/gamefactory/canvas/ICanvasScaler";
import { BaseScalableView } from "by/gamefactory/cityversetycoon/scenes/common/BaseScalableView";
import { GameFont } from "by/gamefactory/cityversetycoon/scenes/common/GameFont";
import { FadeScreen } from "by/gamefactory/cityversetycoon/scenes/game/components/FadeScreen";
import { IDestroyOptions, Sprite, TextStyle, Text } from "pixi.js";
import { EditPriceWarningResourcePackage } from "../resources/EditPriceWarningResourcePackage";
import { ButtonWithScaleAnimationAndText } from "../../../../common/components/ButtonWithScaleAnimationAndText";
import { FullscreenInputBlocker } from "../../../../game/components/FullscreenInputBlocker";
import { gsap } from "gsap";

export class WarningMessagePopupView extends BaseScalableView<boolean> {
    private static readonly HEADER_TEXT = "WARNING";
    private static readonly INFO_TEXT = "YOU ARE ABOUT TO LIST THIS BUILDING\nIN THE MARKETPLACE AT A PRICE THAT IS LOWER\nTHAN OR EQUAL TO YOUR PURCHASE PRICE/\nTHE PRICE YOU PAID FOR IT!\n\nDO YOU WANT TO CONTINUE?";

    private headerText: Text;
    private infoText: Text;
    private fadeScreen: FadeScreen;
    private frame: Sprite;
    private confirmButton: ButtonWithScaleAnimationAndText;
    private cancelButton: ButtonWithScaleAnimationAndText;
    private showAnimation: gsap.core.Timeline;

    public constructor(
        operationControls: PromiseCompletionSource<boolean>,
        private readonly resources: EditPriceWarningResourcePackage,
        canvasScaler: ICanvasScaler,
    ) {
        super(operationControls, canvasScaler);
        this.showAnimation = new gsap.core.Timeline({ paused: true });

        this.build();
        this.layout(this.canvasScaler.referenceWidth, this.canvasScaler.referenceHeight);

        this.confirmButton.onUp.add(this.continueButtonClickHandle, this);
        this.cancelButton.onUp.add(this.cancelButtonClickHandle, this);
    }

    public async start(): Promise<void> {
        this.showAnimation.fromTo(this, { alpha: 0 }, { alpha: 1, duration: 0.2, ease: "power1.in" });
        await this.showAnimation.play();
    }

    private build(): void {
        this.fadeScreen = new FadeScreen(0.1, 0.85, 0.85, this.canvasScaler.referenceWidth, this.canvasScaler.referenceHeight);
        this.fadeScreen.eventMode = "static";

        this.frame = new Sprite(this.resources.assets_views_edit_price_warning_frame_png);
        this.headerText = new Text(WarningMessagePopupView.HEADER_TEXT, new HeaderTextStyle());
        this.infoText = new Text(WarningMessagePopupView.INFO_TEXT, new InfoTextStyle());
        this.confirmButton = new ButtonWithScaleAnimationAndText(this.resources.assets_views_edit_price_warning_green_button_png, "CONTINUE", new ButtonTextStyle());
        this.cancelButton = new ButtonWithScaleAnimationAndText(this.resources.assets_views_edit_price_warning_red_button_png, "CANCEL", new ButtonTextStyle());
        this.addChild(this.fadeScreen, this.frame, this.headerText, this.infoText, this.confirmButton, this.cancelButton);
    }

    private layout(width: number, height: number): void {
        this.frame.position.set(width * 0.5 - this.frame.width * 0.5, height * 0.5 - this.frame.height * 0.5);
        this.headerText.position.set(width * 0.5 - this.headerText.width * 0.5, 639);
        this.infoText.position.set(40, 744);
        this.confirmButton.position.set(211, 1107);
        this.cancelButton.position.set(427, 1107);
    }

    private async continueButtonClickHandle(): Promise<void> {
        const fullScreenInputBlocker = new FullscreenInputBlocker(this.canvasScaler);
        this.addChild(fullScreenInputBlocker);
        await this.showAnimation.reverse();
        this.operationControls.complete(true);
    }

    private async cancelButtonClickHandle(): Promise<void> {
        const fullScreenInputBlocker = new FullscreenInputBlocker(this.canvasScaler);
        this.addChild(fullScreenInputBlocker);
        await this.showAnimation.reverse();
        this.operationControls.complete(false);
    }

    public override destroy(options?: boolean | IDestroyOptions): void {
        this.confirmButton.onUp.remove(this.continueButtonClickHandle, this);
        this.cancelButton.onUp.remove(this.cancelButtonClickHandle, this);
        this.showAnimation.kill();
        super.destroy(options);
    }
}

class HeaderTextStyle extends TextStyle {
    public constructor() {
        super(new TextStyle({
            fill: [
                "#02ffbe",
                "#26ff72"
            ],
            fillGradientType: 1,
            align: "center",
            fontFamily: GameFont.DEFAULT_FONT,
            fontSize: 60
        }));
    }
}

class ButtonTextStyle extends TextStyle {
    public constructor() {
        super({
            fill: "#ffffff",
            fontFamily: GameFont.DEFAULT_FONT,
            fontSize: 34,
            strokeThickness: 1
        });
    }
}

class InfoTextStyle extends TextStyle {
    public constructor() {
        super({
            fill: "#ffffff",
            fontFamily: GameFont.DEFAULT_FONT,
            fontSize: 52,
            align: "center",
            leading: 4
        });
    }
}
